import { useRef } from 'react'
import { AlertTriangle, Info, X } from 'lucide-react'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { useFocusTrap } from '../lib/useFocusTrap'

interface ConfirmModalProps {
  open: boolean
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'warning' | 'info'
  onConfirm: () => void
  onCancel: () => void
}

export default function ConfirmModal({
  open,
  title,
  message,
  confirmText,
  cancelText,
  variant = 'danger',
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  const { t } = useTranslation()
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(dialogRef, onCancel, open)
  if (!open) return null

  const _title = title ?? t('common.confirmation')
  const _confirmText = confirmText ?? t('common.confirm')
  const _cancelText = cancelText ?? t('common.cancel')

  const colors = {
    danger: {
      icon: 'bg-red-500/15 text-red-500',
      button: 'bg-red-600 hover:bg-red-500',
    },
    warning: {
      icon: 'bg-amber-500/15 text-amber-500',
      button: 'bg-amber-600 hover:bg-amber-500',
    },
    info: {
      icon: 'bg-primary-500/15 text-primary-500',
      button: 'bg-primary-600 hover:bg-primary-500',
    },
  }

  const c = colors[variant]
  const IconComponent = variant === 'info' ? Info : AlertTriangle

  return (
    // Backdrop wrapper: click-outside-to-close (modal has a visible close/cancel button)
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Stops backdrop close from firing inside the dialog; onClick only stops propagation */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        ref={dialogRef}
        className="relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center shrink-0', c.icon)}>
              <IconComponent className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 id="confirm-modal-title" className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{_title}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{message}</p>
            </div>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition p-1 -mt-1 -mr-1">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-white/[0.03] border-t border-gray-100 dark:border-white/[0.06]">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-xl hover:bg-gray-50 dark:hover:bg-white/10 transition"
          >
            {_cancelText}
          </button>
          <button
            onClick={() => { onConfirm(); onCancel() }}
            className={clsx('px-4 py-2 text-sm font-medium text-white rounded-xl transition shadow-lg', c.button)}
          >
            {_confirmText}
          </button>
        </div>
      </div>
    </div>
  )
}
